import type { ReactElement } from 'react'
import MuiBox from '@mui/material/Box'
import type { Attachment } from '../../../../types/Document'
import useAttachmentsViewDialogLogic from './useAttachmentsViewDialogLogic'
import { getFileTypeFromName } from './fileType'
import AttachmentContent from './AttachmentContent'
import Dialog from '../../../atoms/feedback/Dialog'
import { FlexRow } from '../../../atoms/layout/Flex'
import IconButton from '../../../atoms/inputs/IconButton'
import { ArrowBackIcon, ArrowForwardIcon, DownloadIcon } from '../../../atoms/display/Icons'
import Link from '../../../atoms/navigation/Link'

/** 添付ファイル表示ダイアログのプロパティ */
export interface AttachmentsViewDialogProps {
  readonly activeId: string
  readonly attachments: readonly Attachment[]
}

export default function AttachmentsViewDialog({
  activeId,
  attachments,
}: AttachmentsViewDialogProps): ReactElement | null {
  const {
    isOpen,
    close,
    currentAttachment,
    goToPrevious,
    goToNext,
    isFirst,
    isLast,
    containerRef,
    contentHeight,
    pdfParams,
    isPC,
  } = useAttachmentsViewDialogLogic({ attachments, initialId: activeId })

  // ダイアログが閉じている、または添付ファイルがない場合は何も表示しない
  if (!isOpen || currentAttachment === undefined) {
    return null
  }

  const fileType = getFileTypeFromName(currentAttachment.name)

  // PC版：左右にナビゲーションボタン、コンテンツは垂直中央配置
  if (isPC) {
    return (
      <Dialog
        open={isOpen}
        width="lg"
        title={currentAttachment.name}
        onClose={close}
        actions={
          <FlexRow>
            <Link href={currentAttachment.url} download>
              <DownloadIcon />
            </Link>
          </FlexRow>
        }
      >
        <MuiBox
          ref={containerRef}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '70vh',
          }}
        >
          <IconButton label="前へ" disabled={isFirst} onClick={goToPrevious}>
            <ArrowBackIcon />
          </IconButton>

          {/* コンテンツ領域：垂直中央配置、overflow: autoでスクロールを内包 */}
          {/* minWidth: 0 はflex itemがコンテンツサイズ以下に縮小することを許可 */}
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              height: '100%',
              minWidth: 0,
              overflow: 'auto',
            }}
          >
            <AttachmentContent
              attachment={currentAttachment}
              fileType={fileType}
              height={contentHeight}
              pdfParams={pdfParams}
              isPC={isPC}
            />
          </MuiBox>

          <IconButton label="次へ" disabled={isLast} onClick={goToNext}>
            <ArrowForwardIcon />
          </IconButton>
        </MuiBox>
      </Dialog>
    )
  }

  // SP版：ナビゲーションボタンは下部、コンテンツはフル幅・垂直中央配置
  return (
    <Dialog fullScreen open={isOpen} title={currentAttachment.name} onClose={close}>
      <MuiBox
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        {/* コンテンツ領域：フル幅・垂直水平中央配置 */}
        {/* minHeight/minWidth: 0 はflex itemがコンテンツサイズ以下に縮小することを許可 */}
        <MuiBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            width: '100%',
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          <AttachmentContent
            attachment={currentAttachment}
            fileType={fileType}
            height={contentHeight}
            pdfParams={pdfParams}
            isPC={isPC}
          />
        </MuiBox>

        {/* 下部ナビゲーション領域 */}
        <MuiBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <IconButton label="前へ" disabled={isFirst} onClick={goToPrevious}>
            <ArrowBackIcon />
          </IconButton>

          <FlexRow align="center" justify="center" space={2}>
            <Link href={currentAttachment.url} download>
              <DownloadIcon />
            </Link>
          </FlexRow>

          <IconButton label="次へ" disabled={isLast} onClick={goToNext}>
            <ArrowForwardIcon />
          </IconButton>
        </MuiBox>
      </MuiBox>
    </Dialog>
  )
}
